import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

/**
 * Custom hook to check and subscribe to maintenance mode status
 * @returns {Object} { maintenanceMode, loading, error, refresh }
 */
export const useMaintenanceMode = () => {
  const [maintenanceMode, setMaintenanceMode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch maintenance mode status
  const fetchMaintenanceMode = useCallback(async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('system_settings')
        .select('setting_value')
        .eq('setting_key', 'maintenance_mode')
        .single();

      if (fetchError) {
        // If table doesn't exist or no data, assume maintenance is off (fail-safe)
        console.warn('Could not fetch maintenance mode:', fetchError);
        setMaintenanceMode({
          enabled: false,
          message: 'We are currently performing scheduled maintenance. We\'ll be back shortly!',
          scheduled_start: null,
          scheduled_end: null,
          allow_staff: false
        });
        setError(null);
        return;
      }

      setMaintenanceMode(data?.setting_value || {
        enabled: false,
        message: 'We are currently performing scheduled maintenance. We\'ll be back shortly!',
        scheduled_start: null,
        scheduled_end: null,
        allow_staff: false
      });
      setError(null);
    } catch (err) {
      console.error('Error fetching maintenance mode:', err);
      // Fail-safe: assume maintenance is off if we can't check
      setMaintenanceMode({
        enabled: false,
        message: 'We are currently performing scheduled maintenance. We\'ll be back shortly!',
        scheduled_start: null,
        scheduled_end: null,
        allow_staff: false
      });
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Manual refresh function
  const refresh = useCallback(() => {
    setLoading(true);
    fetchMaintenanceMode();
  }, [fetchMaintenanceMode]);

  useEffect(() => {
    // Initial fetch
    fetchMaintenanceMode();

    // Set up real-time subscription
    const channel = supabase
      .channel('system_settings_changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'system_settings',
          filter: 'setting_key=eq.maintenance_mode'
        },
        (payload) => {
          console.log('Maintenance mode updated:', payload);
          if (payload.new && payload.new.setting_value) {
            setMaintenanceMode(payload.new.setting_value);
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('Subscribed to maintenance mode changes');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('Error subscribing to maintenance mode changes');
        }
      });

    // Cleanup subscription on unmount
    return () => {
      console.log('Unsubscribing from maintenance mode changes');
      supabase.removeChannel(channel);
    };
  }, [fetchMaintenanceMode]);

  return {
    maintenanceMode,
    loading,
    error,
    refresh
  };
};

/**
 * Hook to check if current user is a system admin
 * @returns {Object} { isAdmin, loading }
 */
export const useIsSystemAdmin = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const checkAdminStatus = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();

        if (!user) {
          if (isMounted) {
            setIsAdmin(false);
            setLoading(false);
          }
          return;
        }

        const { data, error } = await supabase
          .from('accounts')
          .select('is_admin')
          .eq('id', user.id)
          .single();

        if (!isMounted) return;

        if (error) {
          console.error('Error checking admin status:', error);
          setIsAdmin(false);
        } else {
          setIsAdmin(data?.is_admin === true);
        }
      } catch (err) {
        console.error('Error in admin check:', err);
        if (isMounted) setIsAdmin(false);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    // Initial check
    checkAdminStatus();

    // Re-check whenever auth state changes (e.g. an admin logs in from the
    // maintenance page) so the maintenance wrapper updates without a reload
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(() => {
      // Re-check silently (don't toggle the global loading gate — this also
      // fires on token refresh and would otherwise flash the loading screen)
      checkAdminStatus();
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  return { isAdmin, loading };
};

export default useMaintenanceMode;
